import { TSortColumn, TSortDirection } from '../../classes/grid-page-info';
import { IUserPermission, IUserPermissionREST } from '../permissions.interface';

export type IUsersPageParams = {
  X_GROUP_ID?: string;
  page: number;
  pageSize: number;
};

export type IUsersPage = {
  pageSize: number;
  totalCount: number;
  users?: IUser[];
};

export type IUser = IUserGQLBase<
  IUserApplication,
  IUserAccountDTO,
  IUserGroupNested
>;
export type IUserGQL = IUserGQLBase<
  IUsersClientsApps,
  IUserAccount,
  IUserGroup
>;
export type IUserGQLBase<TApplications, TAccounts, TGroups> = {
  // UsersClients
  accessLevel?: AccessLevelsGraphQL;
  accounts?: TAccounts[];
  active: boolean;
  applications?: TApplications[];
  caseNumber?: string;
  cellPhone?: string;
  createdAt?: string;
  createdBy: number;
  ext?: string;
  firstName: string;
  groupId?: string;
  groups?: TGroups[];
  id: number;
  lastName?: string;
  passwordLastUpdated?: string;
  passwordResetAllowed: boolean;
  permissions?: IUserPermission[];
  phone?: string;
  pin?: string;
  role?: string;
  updatedAt?: string;
  updatedBy: number;
  userName: string;
};

export type IUserBaseREST = {
  accessLevel: AccessLevelsREST;
  accounts?: IUserAccountREST[];
  active: boolean;
  applications?: IApplicationREST[];
  caseNumber?: string;
  cellPhone?: string;
  ext?: string;
  firstName: string;
  groups?: IUserGroupNested[];
  lastName?: string;
  passwordResetAllowed?: boolean;
  permissions?: IUserPermissionREST[];
  phone?: string;
  pin?: string;
  role?: string;
};

export type IUserCreateREST = {
  userName: string;
} & IUserBaseREST;

export type IUserUpdateREST = {
  id: number;
} & IUserBaseREST;

export type IUserByGroupResponse = {
  accessLevel: AccessLevelsGraphQL;
  accounts?: IUserAccountREST[];
  applications?: IApplicationREST[];
  firstName: string;
  groups?: IUserGroupNested[];
  id: number;
  lastName: string;
  permissions?: IUserPermissionREST[];
  userName: string;
};

export type IUserWithPermissions = {
  firstName: string;
  id: number;
  lastName: string;
  permissions: IUserPermission[];
  userName: string;
};

export type IUserAccountREST = {
  /** account = accountNumber  */
  account: string;
  name: string;
};

export type IUserApplication = {
  code: string;
  description: string;
};

export type IUsersClientsApps = {
  application: IUserApplication;
};

export type IUserAccount = {
  account: { name: string };
  accountNumber: string;
};

export type IUserAccountDTO = {
  accountNumber: string;
  name: string;
};

export type IUserAccountCheckable = {
  checked: boolean;
} & IUserAccountDTO;

export type IUserGroup = {
  group: IUserGroupNested;
};

export type IUserGroupNested = {
  groupId: string;
  name: string;
};

export type IUserPasswordParams = {
  confirmPassword: string;
  password: string;
  userLinkId: string;
};

export type IUserPasswordUpdateParams = {
  confirmPassword: string;
  oldPassword: string;
  password: string;
  userId: number;
};

export type IApplicationREST = {
  applicationId: string;
  name: string;
};

export const AccessLevelsRESTOptions = {
  ACCOUNT: 'Account',
  BILLING_GROUP: 'BillingGroup',
  SECURE_CONNECT: 'SecureConnect',
  VENDOR: 'Vendor',
} as const;

type TypeAccessLevelsRESTType = typeof AccessLevelsRESTOptions;
export type AccessLevelsRESTKey = keyof TypeAccessLevelsRESTType;
export type AccessLevelsREST =
  TypeAccessLevelsRESTType[keyof TypeAccessLevelsRESTType];

export const AccessLevelsGraphQLOptions = [
  'ACCOUNT',
  'BILLING_GROUP',
  'SECURE_CONNECT',
  'VENDOR',
] as const;

export type AccessLevelsGraphQL = (typeof AccessLevelsGraphQLOptions)[number];

export type IIsAuthorized = {
  authorized: boolean;
};

export class UsersPage implements IUsersPage {
  users?: IUser[] = [];
  totalCount = 0;
  pageSize = 0;
}

export type IGQLUsersParams = {
  after?: string;
  direction?: TSortDirection;
  filter: UserFilters;
  orderby?: TSortColumn;
};

export type IUserValidateParams = {
  pin?: string;
  userLinkUrl: string;
};

export const TStateOptions = ['Active', 'Any', 'Inactive'] as const;

export type TState = (typeof TStateOptions)[number];

export class UserFilters {
  userName?: string;
  firstName?: string;
  lastName?: string;
  groupId?: string;
  vendor?: string;
  ext?: string;
  accessLevel?: AccessLevelsGraphQL;
  role?: string;
  active?: TState;
  passwordResetAllowed?: TState;
  includeInactive?: string[];
}

export type IUserUrlValidationResponse = {
  isValid: boolean;
  validationInfo: string;
};

export type IUserResetPasswordParams = {
  email?: string;
  pin: string;
  requestedBy: 'Admin' | 'User';
  userId?: number;
};

export type IUserByGroupParams = {
  after?: string;
  filter?: UserFilters;
  groups: string[];
};

export type IUsersPermissionsParams = {
  after?: string;
  allowedPermissionsIds: number[];
  filter?: UserFilters;
};

export type IUserByAccountResponse = {
  userGroupsByAccount: IUserByAccount[];
};

export type IUserByAccount = {
  firstName: string;
  groupIds: [
    {
      id: string;
      userClientId: string;
      groupId: string;
      group: {
        groupId: string;
        name: string;
      };
      // usersClients: string,
    },
  ];
  lastName: string;
  userName: string;
};
